import { useAppointmentRepository } from '@/repositories/appointmentRepo'
import { AppointmentEventType, ICreateAppointment } from '@/type/Appointment'
import { isAxiosError } from 'axios'
import { useTranslation } from 'i18next-vue'
import { closeToast, showLoadingToast, showNotify } from 'vant'

export function useAppointmentActions() {
  const { t } = useTranslation()

  const appointmentRepo = useAppointmentRepository()

  async function createAppointment(
    data: ICreateAppointment,
    clinic_uuid: string,
    line_user_id: string,
    channel_id?: string,
  ): Promise<boolean> {
    try {
      await appointmentRepo.createAppointment(data, clinic_uuid, line_user_id, channel_id)
      appointmentRepo.createAppointmentTrickingEvent(AppointmentEventType.CREATE_APPOINTMENT)
      return true
    } catch (err: unknown) {
      if (isAxiosError(err)) {
        if (err.code === 'ERR_NETWORK') {
          showNotify({ type: 'danger', message: t('internet_connection_error') })
          return false
        } else if (err.response?.status === 400 || err.response?.status === 404) {
          if (err.response.data.code === 'limit') {
            showNotify({
              type: 'danger',
              message: 'ไม่สามารถสร้างนัดหมายได้ เนื่องจากจำนวนนัดหมายต่อวันมากกว่าที่กำหนด',
            })
          } else {
            if (typeof err.response.data === 'string') {
              showNotify({ type: 'danger', message: err.response.data })
            } else if (typeof err.response.data === 'object' && err.response.data !== null) {
              const firstKey = Object.keys(err.response.data)[0]
              const firstError = err.response.data[firstKey]
              const message = Array.isArray(firstError) ? firstError[0] : firstError
              // non_field_errors เป็นข้อความที่อ่านได้เลย (เช่น บริการที่เลือกถูกปิด - LMP-7840) ไม่ใส่ชื่อ key นำหน้า
              // ต้องเป็น else: showNotify แสดงทีละอัน อันหลังจะทับข้อความแรก
              if (firstKey === 'non_field_errors') {
                showNotify({ type: 'danger', message })
              } else {
                showNotify({ type: 'danger', message: `${firstKey}: ${message}` })
              }
            } else if (err.response.data) {
              showNotify({ type: 'danger', message: err.response.data })
            } else {
              showNotify({ type: 'danger', message: t('unknown_error') })
            }
          }
        }
      }
      return false
    }
  }

  const cancelAppointment = async (appointment_uuid: string) => {
    try {
      showLoadingToast({
        message: 'กำลังยกเลิกนัดหมาย...',
        forbidClick: true,
        duration: 0,
        loadingType: 'spinner',
        overlay: true,
      })
      await appointmentRepo.cancelAppointment(appointment_uuid)
      showNotify({
        type: 'success',
        message: 'ยกเลิกนัดหมายสำเร็จ',
        duration: 5000,
      })
      return true
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 400) {
        showNotify({
          type: 'danger',
          message: error.response.data[0] || 'ไม่สามารถยกเลิกนัดหมายได้',
          duration: 5000,
        })
      } else {
        showNotify({
          type: 'danger',
          message: 'ไม่สามารถยกเลิกนัดหมายได้',
          duration: 5000,
        })
      }
      console.error('Error cancelling appointment:', error)
      return false
    } finally {
      // close loading toast
      closeToast()
    }
  }

  async function postponeAppointment(params: {
    appointmentUuid: string
    clinicId: string
    lineUserId: string
    channelId: string
    data: ICreateAppointment
  }): Promise<{ success: boolean; isLimit?: boolean }> {
    try {
      await appointmentRepo.postponeAppointment(params)
      showNotify({
        type: 'success',
        message: 'เลื่อนนัดหมายสำเร็จ',
      })
      appointmentRepo.createAppointmentTrickingEvent(AppointmentEventType.POSTPONE_APPOINTMENT)
      return { success: true }
    } catch (err: unknown) {
      if (isAxiosError(err)) {
        if (err.response?.data?.code === 'limit') {
          return { success: false, isLimit: true }
        }
        const errorMsg = err.response?.data?.[0] || err.response?.data || t('unknown_error')
        showNotify({ type: 'danger', message: errorMsg })
      } else {
        showNotify({ type: 'danger', message: t('unknown_error') })
      }
      return { success: false }
    }
  }

  return {
    createAppointment,
    cancelAppointment,
    postponeAppointment,
  }
}
